"use client";

import { BiSearch } from "react-icons/bi";
import { format, parseISO } from "date-fns";
import unorm from "unorm";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatISO } from "date-fns";
import qs from "query-string";

import CalendarSearch from "../inputs/CalendarSearch";
import { toast } from "react-hot-toast";
import { MdMyLocation } from "react-icons/md";
import { IoLocationOutline } from "react-icons/io5";
import { IoMdClose } from "react-icons/io";
import convertPrice from "@/app/utils/CurrencyConverter";
import { ExchangeCurrency } from "@prisma/client";
import { SimplifiedUser } from "@/app/types";
import useCurrencies from "@/app/hooks/useCurrencies";

interface QueryParams {
  [key: string]: string | undefined;
}

interface SearchProps {
  currentUser?: SimplifiedUser | null;
  rates?: ExchangeCurrency | null;
}

const Search: React.FC<SearchProps> = ({ currentUser, rates }) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const userCurrency = currentUser?.currency || "USD";
  const { getByCode } = useCurrencies();
  const currentCode = getByCode(currentUser?.currency as string);
  const newCurrencySymbol = currentCode?.symbol || "$";

  const paramsDate = searchParams?.get("date");
  const paramsCityOrCountry = searchParams?.get("cityOrCountry");
  const paramsPrice = searchParams?.get("maxPrice");

  const searchContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const locationMenuRef = useRef<HTMLDivElement>(null);

  const [maxPrice, setMaxPrice] = useState(
    paramsPrice ? parseInt(paramsPrice) : 0
  );
  const [displayPrice, setDisplayPrice] = useState(
    paramsPrice ? parseInt(paramsPrice) : 0
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    paramsDate ? parseISO(paramsDate) : null
  );
  const [cityOrCountry, setCityOrCountry] = useState<string>(
    paramsCityOrCountry || ""
  );
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [hasSearch, setHasSearch] = useState(false);

  // useEffect(() => {
  //   const convertParamsPrice = async () => {
  //     if (paramsPrice && userCurrency !== "USD" && rates) {
  //       try {
  //         const localPrice = await convertPrice(parseInt(paramsPrice), "USD", userCurrency, rates);
  //         setDisplayPrice(Math.round(localPrice));
  //       } catch (error) {
  //         console.error("Error converting price:", error);
  //         setDisplayPrice(parseInt(paramsPrice));
  //       }
  //     } else if (paramsPrice) {
  //       setDisplayPrice(parseInt(paramsPrice));
  //     }
  //   };

  //   convertParamsPrice();
  // }, [paramsPrice, userCurrency, rates]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isOutsideSearch =
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node);

      const isOutsideMenu =
        menuRef.current && !menuRef.current.contains(event.target as Node);

      const isOutsideLocationMenu =
        locationMenuRef.current &&
        !locationMenuRef.current.contains(event.target as Node);

      if (
        isOutsideSearch &&
        (activeSection !== "location" ||
          (activeSection === "location" && isOutsideLocationMenu)) &&
        ((activeSection !== "dates" && activeSection !== "budget") ||
          isOutsideMenu)
      ) {
        setActiveSection(null);
      }
    };

    if (activeSection) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [activeSection]);

  useEffect(() => {
    if (paramsDate) {
      setSelectedDate(parseISO(paramsDate));
    } else {
      setSelectedDate(null);
    }

    if (paramsCityOrCountry) {
      setCityOrCountry(paramsCityOrCountry);
    } else {
      setCityOrCountry("");
    }

    if (paramsPrice) {
      setMaxPrice(parseInt(paramsPrice));
    } else {
      setMaxPrice(0);
      setDisplayPrice(0);
    }
  }, [paramsDate, paramsCityOrCountry, paramsPrice]);

  const clearFilter = (filterName: string) => {
    let currentQuery: QueryParams = {};

    if (searchParams) {
      currentQuery = qs.parse(searchParams.toString()) as QueryParams;
      delete currentQuery[filterName];
    }

    const url = qs.stringifyUrl(
      {
        url: pathname || "/search",
        query: currentQuery,
      },
      { skipNull: true }
    );

    router.push(url);
    router.refresh();
  };

  const resetAllFilters = () => {
    router.push("/");
    router.refresh();
    setSelectedDate(null);
    setMaxPrice(0);
    setDisplayPrice(0);
    setCityOrCountry("");
  };

  const handleSectionClick = (sectionName: string) => {
    if (activeSection === sectionName) {
      setActiveSection(null);
    } else {
      setActiveSection(sectionName);
    }
  };

  const capitalizeEachWord = (str: string): string => {
    if (!str) return str;
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const locationLabel = useMemo(() => {
    if (cityOrCountry) {
      return capitalizeEachWord(cityOrCountry);
    }

    return "Anywhere";
  }, [cityOrCountry]);

  const durationLabel = useMemo(() => {
    if (selectedDate) {
      return format(selectedDate, "d/MM/yy");
    }
    return "Date";
  }, [selectedDate]);

  const maxPriceLabel = useMemo(() => {
    if (displayPrice) {
      const formattedPrice = displayPrice.toLocaleString();
      return (
        <div className="flex items-center justify-center">
          <span className="mr-1">{newCurrencySymbol}</span>
          <span className="truncate max-w-[60px] sm:max-w-[80px]">
            {formattedPrice}
          </span>
        </div>
      );
    }
    return "Budget";
  }, [displayPrice, newCurrencySymbol]);

  const onSubmit = useCallback(async () => {
    let currentQuery: QueryParams = {};

    if (searchParams) {
      currentQuery = qs.parse(searchParams.toString()) as QueryParams;
    }

    const updatedQuery: QueryParams = {
      ...currentQuery,
    };

    // Only add params if they have values
    if (cityOrCountry) {
      updatedQuery.cityOrCountry = cityOrCountry;
    } else {
      delete updatedQuery.cityOrCountry;
    }

    if (selectedDate) {
      updatedQuery.date = formatISO(selectedDate);
    } else {
      delete updatedQuery.date;
    }

    if (displayPrice > 0) {
      if (userCurrency !== "USD" && rates) {
        try {
          // Convert the display price (in user currency) to USD for storage in URL
          const usdPrice = await convertPrice(
            displayPrice,
            userCurrency,
            "USD",
            rates
          );
          updatedQuery.maxPrice = Math.round(usdPrice).toString();
        } catch (error) {
          console.error("Error converting price:", error);
          updatedQuery.maxPrice = displayPrice.toString();
        }
      } else {
        updatedQuery.maxPrice = displayPrice.toString();
      }
    } else {
      delete updatedQuery.maxPrice;
    }

    const url = qs.stringifyUrl(
      {
        url: "/search",
        query: updatedQuery,
      },
      { skipNull: true }
    );

    router.push(url);
    router.refresh();
    setActiveSection(null);
    setHasSearch(true);
  }, [
    router,
    searchParams,
    displayPrice,
    selectedDate,
    cityOrCountry,
    userCurrency,
    rates,
    pathname,
  ]);

  function normalizeString(input: string): string {
    return unorm
      .nfd(input)
      .replace(/[\u0300-\u036f]/g, "")
      .replace(
        /\w\S*/g,
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );
  }

  const searchNearby = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();

            if (data.address) {
              const rawCity =
                data.address.city || data.address.municipality || "";
              const normalizedCity = normalizeString(rawCity);
              const rawCountry = data.address.country || "";
              const normalizedCountry = normalizeString(rawCountry);

              // Set the combined location
              const location = normalizedCity || normalizedCountry;
              setCityOrCountry(location);

              // Update URL
              let currentQuery: QueryParams = {};
              if (searchParams) {
                currentQuery = qs.parse(searchParams.toString()) as QueryParams;
              }

              const updatedQuery = {
                ...currentQuery,
                cityOrCountry: location,
              };

              const url = qs.stringifyUrl(
                {
                  url: "/search",
                  query: updatedQuery,
                },
                { skipNull: true }
              );

              router.push(url);
            } else {
              toast.error("Location not found");
            }
          } catch (error) {
            console.error("Error fetching geocoding data:", error);
            toast.error("Error getting location");
          }
        },
        (error) => {
          console.error("Error obtaining location:", error.message);
          toast.error("Could not access your location");
        }
      );
    } else {
      toast.error("Geolocation is not supported in this browser");
    }
  };

  return (
    <div>
      <div className="flex items-center mt-3">
        <div className="inline-flex items-center justify-center w-full">
          <span className="mt-3 absolute rounded-lg font-medium text-gray-900 -translate-x-1/2 bg-white left-1/2">
            <div
              ref={searchContainerRef}
              className="border-[2px] w-full md:w-auto rounded-lg shadow-sm hover:shadow-md transition cursor-pointer"
            >
              <div className="flex flex-row items-center justify-between">
                <div
                  className={`text-sm font-semibold sm:w-[120px] w-[90px] sm:px-6 px-1 flex-1 text-center`}
                  onClick={() => handleSectionClick("location")}
                >
                  {locationLabel}
                </div>
                <div
                  className={`text-sm font-semibold sm:w-[120px] w-[80px] sm:px-6 px-1 border-x-[1px] flex-1 text-center`}
                  onClick={() => handleSectionClick("dates")}
                >
                  {durationLabel}
                </div>
                <div
                  className={`text-sm font-semibold sm:w-[120px] w-[70px] sm:px-6 px-1 flex-1 text-center`}
                  onClick={() => handleSectionClick("budget")}
                >
                  {maxPriceLabel}
                </div>
                <div
                  onClick={onSubmit}
                  className="sm:hidden block text-sm p-3 bg-cyan-700 hover:bg-cyan-600 text-white flex-row items-center gap-3 font-bold rounded-r-lg"
                >
                  <BiSearch size={18} />
                </div>
                <div
                  onClick={onSubmit}
                  className="hidden sm:block text-sm py-3 px-6 bg-cyan-700 hover:bg-cyan-600 text-white flex-row items-center gap-3 font-bold rounded-r-lg"
                >
                  Search
                </div>
              </div>
            </div>
          </span>
        </div>
      </div>

      {activeSection === "location" && (
        <div
          ref={locationMenuRef}
          className="p-4 mt-5 border-[2px] max-w-[450px] sm:w-2/3 shadow-sm hover:shadow-md transition cursor-pointer absolute rounded-lg font-medium text-gray-900 -translate-x-1/2 bg-white left-1/2"
        >
          <div className="relative flex items-center border border-gray-300 rounded-md p-2">
            <IoLocationOutline size={20} className="text-gray-500 mr-2" />
            <input
              type="text"
              placeholder="Search by city or country"
              value={cityOrCountry}
              onChange={(e) => setCityOrCountry(e.target.value)}
              className="w-full border-none outline-none pr-8 text-sm focus:outline-none focus:ring-cyan-700 focus:border-cyan-700"
            />
            {cityOrCountry && (
              <button
                type="button"
                onClick={() => {
                  setCityOrCountry("");
                  clearFilter("cityOrCountry");
                }}
                className="absolute top-1/2 transform -translate-y-1/2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <IoMdClose size={20} />
              </button>
            )}
          </div>
          <div
            className="mt-4 ml-1 flex flex-row text-cyan-700 cursor-pointer"
            onClick={searchNearby}
          >
            <MdMyLocation size={18} />
            <span className="ml-2 hover:underline text-sm">Search Nearby</span>
          </div>
        </div>
      )}

      {activeSection === "dates" && (
        <div
          ref={menuRef}
          className="p-4 mt-5 border-[2px] max-w-[350px] sm:w-2/3 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer absolute font-medium text-gray-900 -translate-x-1/2 bg-white left-1/2"
        >
          <CalendarSearch
            selectedDate={selectedDate !== null ? selectedDate : undefined}
            onChange={(value) => setSelectedDate(value)}
          />
        </div>
      )}

      {activeSection === "budget" && (
        <div
          ref={menuRef}
          className="p-4 mt-5 border-[2px] max-w-[450px] sm:w-2/3 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer absolute font-medium text-gray-900 -translate-x-1/2 bg-white left-1/2"
        >
          <div className="mb-4">
            <label
              htmlFor="price-input"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Max Budget ({newCurrencySymbol})
            </label>
            <div className="relative">
              <input
                id="price-input"
                type="number"
                min="0"
                value={displayPrice || ""}
                onChange={(e) => setDisplayPrice(parseInt(e.target.value) || 0)}
                placeholder={`Enter amount in ${newCurrencySymbol}`}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-700 focus:border-cyan-700 pr-10"
              />
              {displayPrice > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setDisplayPrice(0);
                    clearFilter("maxPrice");
                  }}
                  className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
                  style={{ right: "20px" }}
                >
                  <IoMdClose size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {(cityOrCountry || selectedDate || displayPrice > 0) && (
        <div className="mt-2 flex justify-center">
          <button
            onClick={resetAllFilters}
            className="text-xs text-cyan-700 hover:underline"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
};

export default Search;